import { client, handleRequestBody } from "./client.ts";

export interface TimeEntryWorker {
	id: string;
	name: string;
	job_title?: string;
	role?: string;
	user_id: string | null;
}

export interface TimeEntryEdit {
	by?: string;
	at: string;
	note?: string;
}

export interface TimeEntryLocation {
	lat: number;
	lng: number;
	accuracy_m: number | null;
	distance_m: number | null;
}

export interface ClockLocationInput {
	lat: number;
	lng: number;
	accuracyM: number | null;
}

export type TimeEntryMethod = "app" | "kiosk" | "manual";

export interface TimeEntry {
	id: string;
	worker: TimeEntryWorker;
	work_date: string;
	clocked_in_at: string;
	clocked_out_at: string | null;
	clock_in_location: TimeEntryLocation | null;
	clock_out_location: TimeEntryLocation | null;
	total_minutes: number | null;
	method: TimeEntryMethod;
	note: string;
	edited: TimeEntryEdit | null;
	created_at: string;
	updated_at: string;
}

interface TimeEntryResponse {
	entry: TimeEntry;
}

export interface ListTimeEntriesParams {
	from: string;
	to: string;
	page?: number;
	pageSize?: number;
}

export interface ListTimeEntriesResponse {
	count: number;
	items: TimeEntry[];
}

const MAX_PAGE_SIZE = 100;

function clockBody(location: ClockLocationInput | null | undefined) {
	if (!location) return undefined;
	return {
		location: {
			lat: location.lat,
			lng: location.lng,
			accuracy_m: location.accuracyM ?? undefined,
		},
	};
}

export async function clockIn(
	worksiteId: string,
	location?: ClockLocationInput | null,
): Promise<TimeEntry> {
	const { entry } = await handleRequestBody(() =>
		client.post<TimeEntryResponse>(
			`/worksites/${worksiteId}/time-entries/clock-in`,
			clockBody(location),
		),
	);
	return entry;
}

export async function clockOut(
	worksiteId: string,
	location?: ClockLocationInput | null,
): Promise<TimeEntry> {
	const { entry } = await handleRequestBody(() =>
		client.post<TimeEntryResponse>(
			`/worksites/${worksiteId}/time-entries/clock-out`,
			clockBody(location),
		),
	);
	return entry;
}

export function listMyTimeEntries(
	worksiteId: string,
	params: ListTimeEntriesParams,
): Promise<ListTimeEntriesResponse> {
	return handleRequestBody(() =>
		client.get<ListTimeEntriesResponse>(
			`/worksites/${worksiteId}/time-entries/me`,
			{
				params: {
					from: params.from,
					to: params.to,
					page: params.page ?? 1,
					page_size: params.pageSize ?? MAX_PAGE_SIZE,
				},
			},
		),
	);
}

export function listTeamTimeEntries(
	worksiteId: string,
	params: ListTimeEntriesParams,
): Promise<ListTimeEntriesResponse> {
	return handleRequestBody(() =>
		client.get<ListTimeEntriesResponse>(
			`/worksites/${worksiteId}/time-entries`,
			{
				params: {
					from: params.from,
					to: params.to,
					page: params.page ?? 1,
					page_size: params.pageSize ?? MAX_PAGE_SIZE,
				},
			},
		),
	);
}

export interface CreateTimeEntryInput {
	workerId: string;
	workDate: string;
	clockedInAt: string;
	clockedOutAt?: string;
	note?: string;
	editNote: string;
}

export async function createTimeEntry(
	worksiteId: string,
	input: CreateTimeEntryInput,
): Promise<TimeEntry> {
	const { entry } = await handleRequestBody(() =>
		client.post<TimeEntryResponse>(`/worksites/${worksiteId}/time-entries`, {
			worker_id: input.workerId,
			work_date: input.workDate,
			clocked_in_at: input.clockedInAt,
			clocked_out_at: input.clockedOutAt,
			note: input.note,
			edit_note: input.editNote,
		}),
	);
	return entry;
}

export interface UpdateTimeEntryInput {
	clockedInAt?: string;
	clockedOutAt?: string;
	note?: string;
	editNote: string;
}

export async function updateTimeEntry(
	worksiteId: string,
	entryId: string,
	input: UpdateTimeEntryInput,
): Promise<TimeEntry> {
	const { entry } = await handleRequestBody(() =>
		client.patch<TimeEntryResponse>(
			`/worksites/${worksiteId}/time-entries/${entryId}`,
			{
				clocked_in_at: input.clockedInAt,
				clocked_out_at: input.clockedOutAt,
				note: input.note,
				edit_note: input.editNote,
			},
		),
	);
	return entry;
}
